import { useFeatureFlagEnabled } from 'posthog-js/react';

import { hasAppAccess } from 'entities/user/lib/hasAppAccess';
import { useAuth } from 'shared/lib/auth/useAuth';
import { FeatureFlag } from 'shared/lib/features/common';
import { useScreenSize } from 'shared/lib/screen/useScreenSize';

/**
 * Whether the assistant chat is available to this user: on a wide enough screen, in the rollout,
 * and past AuthGuard's interstitials (its endpoints answer 403 for accounts that haven't been
 * approved).
 *
 * Shared so the launcher, the dialog and the Inkeep widget can't disagree about it — when they did,
 * the dashboard showed two "Ask Truv AI" buttons stacked in the corner, each opening a different
 * chat (CL-6818). That is also why a phone is answered here rather than at each mount: the chat is
 * a docked side panel or a floating pill, neither of which a phone has room for, and gating it at
 * the source stops the dialog, its onboarding-state request and the panel's width reservation
 * along with the launcher.
 */
export const useAssistantEnabled = (): boolean => {
  const { user } = useAuth();
  const assistantEnabled = useFeatureFlagEnabled(FeatureFlag.AI_ASSISTANT);
  const { isMobile } = useScreenSize();

  return !isMobile && hasAppAccess(user) && Boolean(assistantEnabled);
};
